import React, { useState } from 'react';
import classnames from 'classnames/bind';
import { MessageSquare, Check, Trash2, Send, X } from 'lucide-react';

import { CommentThread } from '@INTERFACES/comments';

import styles from './CommentThreadPanel.module.scss';

const cx: CX = classnames.bind(styles);

interface Props {
    thread: CommentThread;
    onAddComment: (threadId: string, commentText: string) => void;
    onToggleStatus: (threadId: string) => void;
    onDeleteThread: (threadId: string) => void;
    onClose: () => void;
}

export default function CommentThreadPanel({
    thread,
    onAddComment,
    onToggleStatus,
    onDeleteThread,
    onClose
}: Props) {
    const [newCommentText, setNewCommentText] = useState('');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (newCommentText.trim()) {
            onAddComment(thread.id, newCommentText.trim());
            setNewCommentText('');
        }
    };

    const formatDate = (date: Date) => new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);

    return (
        <div className={cx('comment-thread-panel')}>
            <div className={cx('panel-header')}>
                <div className={cx('header-content')}>
                    <MessageSquare size={16} />
                    <span className={cx('thread-title')}>
                        {thread.status === 'resolved' ? 'Resolved' : 'Open'} Comment
                    </span>
                </div>
                <button
                    type="button"
                    className={cx('close-button')}
                    onClick={onClose}
                >
                    <X size={16} />
                </button>
            </div>

            <div className={cx('selected-text-preview')}>
                <div className={cx('quote-indicator')} />
                <span>&ldquo;{thread.selectedText}&rdquo;</span>
            </div>

            <div className={cx('comments-list')}>
                {thread.comments.map((comment) => (
                    <div key={comment.id} className={cx('comment-item')}>
                        <div className={cx('comment-header')}>
                            <div className={cx('author-info')}>
                                <div className={cx('author-avatar')}>
                                    {comment.author.name.charAt(0).toUpperCase()}
                                </div>
                                <div className={cx('author-details')}>
                                    <span className={cx('author-name')}>{comment.author.name}</span>
                                    <span className={cx('comment-date')}>
                                        {formatDate(comment.createdAt)}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className={cx('comment-text')}>
                            {comment.text}
                        </div>
                    </div>
                ))}
            </div>

            {thread.status === 'open' && (
                <div className={cx('add-comment-section')}>
                    <form onSubmit={handleSubmit} className={cx('comment-form')}>
                        <textarea
                            value={newCommentText}
                            onChange={(e) => setNewCommentText(e.target.value)}
                            placeholder="Add a reply..."
                            className={cx('comment-input')}
                            rows={3}
                        />
                        <div className={cx('form-actions')}>
                            <button
                                type="submit"
                                disabled={!newCommentText.trim()}
                                className={cx('submit-button')}
                            >
                                <Send size={14} />
                                Reply
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className={cx('thread-actions')}>
                <button
                    type="button"
                    onClick={() => onToggleStatus(thread.id)}
                    className={cx('status-button', {
                        'resolve-button': thread.status === 'open',
                        'reopen-button': thread.status === 'resolved',
                    })}
                >
                    <Check size={14} />
                    {thread.status === 'open' ? 'Resolve' : 'Reopen'}
                </button>
                <button
                    type="button"
                    onClick={() => onDeleteThread(thread.id)}
                    className={cx('delete-button')}
                >
                    <Trash2 size={14} />
                    Delete
                </button>
            </div>
        </div>
    );
} 